import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Play, Pause, RotateCcw, SkipForward, SkipBack, Volume2, VolumeX,
  BookOpen, FileText, ChevronLeft, Settings2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import InteractiveWhiteboard from "@/components/lesson/InteractiveWhiteboard";

const DIALECT_OPTIONS = [
  { value: "ar-EG", label: "🇪🇬 مصرية" },
  { value: "ar-SA", label: "🇸🇦 سعودية" },
  { value: "ar-AE", label: "🇦🇪 خليجية" },
] as const;

type DialectCode = typeof DIALECT_OPTIONS[number]["value"];

interface LessonPlayerProps {
  lesson: {
    id: string;
    title: string;
    content: string | null;
    media_url: string | null;
    nursery_id: string;
  };
  onClose: () => void;
}

export default function LessonPlayer({ lesson, onClose }: LessonPlayerProps) {
  const [sections, setSections] = useState<string[]>([]);
  const [currentSection, setCurrentSection] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [dialect, setDialect] = useState<DialectCode>(() => {
    return (localStorage.getItem("lesson-dialect") as DialectCode) || "ar-EG";
  });
  const [showSettings, setShowSettings] = useState(false);
  const [linkedExam, setLinkedExam] = useState<any>(null);
  const [loadingExam, setLoadingExam] = useState(false);
  const [availableArVoices, setAvailableArVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [noArabicVoice, setNoArabicVoice] = useState(false);
  const synthRef = useRef(window.speechSynthesis);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const navigate = useNavigate();

  // Ensure voices are loaded before speaking
  useEffect(() => {
    const loadVoices = () => {
      const voices = synthRef.current.getVoices();
      const arVoices = voices.filter((v) => v.lang.startsWith("ar"));
      console.log("All voices:", voices.map(v => `${v.name} (${v.lang})`));
      console.log("Arabic voices:", arVoices.map(v => `${v.name} (${v.lang})`));
      setAvailableArVoices(arVoices);
      if (voices.length > 0 && arVoices.length === 0) {
        setNoArabicVoice(true);
      }
    };
    loadVoices();
    speechSynthesis.addEventListener("voiceschanged", loadVoices);
    return () => speechSynthesis.removeEventListener("voiceschanged", loadVoices);
  }, []);

  // Split content into sections by paragraphs
  useEffect(() => {
    if (lesson.content) {
      const parts = lesson.content
        .split(/\n\n+|\n(?=[^\n])/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      setSections(parts.length > 0 ? parts : [lesson.content]);
    } else {
      setSections(["لا يوجد محتوى لهذا الدرس"]);
    }
  }, [lesson.content]);

  // Check for linked exam
  useEffect(() => {
    const fetchExam = async () => {
      setLoadingExam(true);
      const { data } = await supabase
        .from("exams")
        .select("id, title, is_interactive, max_score, duration_minutes")
        .eq("lesson_id", lesson.id)
        .eq("nursery_id", lesson.nursery_id)
        .limit(1);
      if (data && data.length > 0) setLinkedExam(data[0]);
      setLoadingExam(false);
    };
    fetchExam();
  }, [lesson.id, lesson.nursery_id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      synthRef.current.cancel();
    };
  }, []);

  const speak = useCallback(
    (text: string) => {
      synthRef.current.cancel();

      const voices = synthRef.current.getVoices();
      
      // Find Arabic voice - prioritize exact dialect match
      const exactVoice = voices.find(
        (v) => v.lang === dialect || v.lang === dialect.replace("-", "_")
      );
      const arGoogleVoice = voices.find(
        (v) => v.lang.startsWith("ar") && v.name.toLowerCase().includes("google")
      );
      const anyArVoice = voices.find((v) => v.lang.startsWith("ar"));
      const arVoice = exactVoice || arGoogleVoice || anyArVoice;

      const utterance = new SpeechSynthesisUtterance(text);
      
      // MUST set voice BEFORE lang to ensure Arabic is used
      if (arVoice) {
        utterance.voice = arVoice;
        utterance.lang = arVoice.lang;
      } else {
        utterance.lang = dialect;
      }
      
      utterance.rate = 0.85;
      utterance.pitch = 1.1;
      utterance.volume = isMuted ? 0 : 1;

      utterance.onend = () => {
        if (currentSection < sections.length - 1) {
          setCurrentSection((prev) => prev + 1);
        } else {
          setIsPlaying(false);
          setFinished(true);
        }
      };

      utteranceRef.current = utterance;
      synthRef.current.speak(utterance);
    },
    [isMuted, currentSection, sections.length, dialect]
  );

  // Auto-play when section changes and isPlaying
  useEffect(() => {
    if (isPlaying && sections[currentSection]) {
      speak(sections[currentSection]);
    }
  }, [currentSection, isPlaying, sections, speak]);

  const handlePlayPause = () => {
    if (isPlaying) {
      synthRef.current.cancel();
      setIsPlaying(false);
    } else {
      setFinished(false);
      setIsPlaying(true);
      if (!synthRef.current.speaking) {
        speak(sections[currentSection]);
      }
    }
  };

  const handleReplay = () => {
    synthRef.current.cancel();
    setCurrentSection(0);
    setFinished(false);
    setIsPlaying(true);
  };

  const handleNext = () => {
    if (currentSection < sections.length - 1) {
      synthRef.current.cancel();
      setCurrentSection((prev) => prev + 1);
      if (!isPlaying) setIsPlaying(true);
    }
  };

  const handlePrev = () => {
    if (currentSection > 0) {
      synthRef.current.cancel();
      setCurrentSection((prev) => prev - 1);
      if (!isPlaying) setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    setIsMuted((m) => !m);
    if (utteranceRef.current) {
      utteranceRef.current.volume = isMuted ? 1 : 0;
    }
  };

  const handleDialectChange = (value: string) => {
    const newDialect = value as DialectCode;
    setDialect(newDialect);
    localStorage.setItem("lesson-dialect", newDialect);
    // Restart current section with new dialect
    if (isPlaying) {
      synthRef.current.cancel();
      setTimeout(() => speak(sections[currentSection]), 100);
    }
  };

  const progress = sections.length > 0
    ? ((currentSection + (finished ? 1 : 0)) / sections.length) * 100
    : 0;

  const goToExam = () => {
    if (linkedExam?.is_interactive) {
      navigate(`/dashboard/take-exam/${linkedExam.id}`);
    }
  };

  return (
    <div className="space-y-4" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onClose} className="gap-1">
          <ChevronLeft className="w-4 h-4" />
          رجوع
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setShowSettings((s) => !s)}
          >
            <Settings2 className="w-4 h-4" />
          </Button>
          <Badge variant="secondary" className="text-xs">
            الجزء {currentSection + 1} من {sections.length}
          </Badge>
        </div>
      </div>

      {/* No Arabic voice warning */}
      {noArabicVoice && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive">
          <p className="font-semibold mb-1">⚠️ لا توجد أصوات عربية في متصفحك</p>
          <p className="text-xs text-destructive/80">
            يرجى استخدام <strong>Google Chrome</strong> على جهاز كمبيوتر أو هاتف أندرويد للحصول على الصوت العربي.
            المتصفح الحالي لا يدعم الأصوات العربية.
          </p>
        </div>
      )}

      {/* Dialect Settings */}
      {showSettings && (
        <Card className="border-dashed">
          <CardContent className="p-3 flex items-center gap-3">
            <span className="text-sm font-medium text-foreground shrink-0">🗣️ اللهجة:</span>
            <Select value={dialect} onValueChange={handleDialectChange}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIALECT_OPTIONS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Lesson Image */}
      {lesson.media_url && (
        <div className="relative rounded-xl overflow-hidden">
          <img
            src={lesson.media_url}
            alt={lesson.title}
            className="w-full h-48 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <h2 className="absolute bottom-3 right-4 text-white font-bold text-lg">
            {lesson.title}
          </h2>
        </div>
      )}

      {!lesson.media_url && (
        <div className="bg-primary/10 rounded-xl p-6 text-center">
          <BookOpen className="w-10 h-10 text-primary mx-auto mb-2" />
          <h2 className="font-bold text-lg text-foreground">{lesson.title}</h2>
        </div>
      )}

      {/* Progress */}
      <div className="space-y-1">
        <Progress value={progress} className="h-2" />
        <p className="text-[10px] text-muted-foreground text-center">
          {Math.round(progress)}% مكتمل
        </p>
      </div>

      {/* Current Section Content */}
      <Card className={`transition-all duration-300 ${isPlaying ? "ring-2 ring-primary/30 shadow-lg" : ""}`}>
        <CardContent className="p-5">
          <div className="flex items-start gap-2 mb-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isPlaying ? "bg-primary text-primary-foreground animate-pulse" : "bg-muted"}`}>
              <FileText className="w-4 h-4" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">
              الجزء {currentSection + 1}
            </p>
          </div>
          <p className="text-base leading-relaxed text-foreground whitespace-pre-wrap">
            {sections[currentSection]}
          </p>
        </CardContent>
      </Card>

      {/* Interactive Whiteboard */}
      <Card>
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <span className="text-xl">📝</span>
            السبورة التفاعلية - ارسم واكتب!
          </div>
          <InteractiveWhiteboard height={250} />
        </CardContent>
      </Card>

      {/* Controls */}
      <div className="flex items-center justify-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleMute}
          className="h-10 w-10"
        >
          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={handlePrev}
          disabled={currentSection === 0}
          className="h-10 w-10"
        >
          <SkipForward className="w-5 h-5" />
        </Button>

        <Button
          onClick={handlePlayPause}
          size="icon"
          className="h-14 w-14 rounded-full shadow-lg"
        >
          {isPlaying ? (
            <Pause className="w-6 h-6" />
          ) : (
            <Play className="w-6 h-6 mr-[-2px]" />
          )}
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={handleNext}
          disabled={currentSection >= sections.length - 1}
          className="h-10 w-10"
        >
          <SkipBack className="w-5 h-5" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={handleReplay}
          className="h-10 w-10"
        >
          <RotateCcw className="w-5 h-5" />
        </Button>
      </div>

      {/* Section dots */}
      <div className="flex items-center justify-center gap-1.5 flex-wrap">
        {sections.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              synthRef.current.cancel();
              setCurrentSection(i);
              setFinished(false);
              if (isPlaying) speak(sections[i]);
            }}
            className={`w-2.5 h-2.5 rounded-full transition-all ${
              i === currentSection
                ? "bg-primary scale-125"
                : i < currentSection
                ? "bg-primary/40"
                : "bg-muted-foreground/20"
            }`}
          />
        ))}
      </div>

      {/* Finished state + Exam link */}
      {finished && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-5 text-center space-y-3">
            <div className="text-3xl">🎉</div>
            <p className="font-semibold text-foreground">أحسنت! أنهيت الاستماع للدرس</p>

            {linkedExam ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  هل أنت مستعد للامتحان؟
                </p>
                <Button onClick={goToExam} className="gap-2">
                  <BookOpen className="w-4 h-4" />
                  ابدأ الامتحان: {linkedExam.title}
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                لا يوجد امتحان مرتبط بهذا الدرس حالياً
              </p>
            )}

            <Button variant="outline" size="sm" onClick={handleReplay} className="gap-1">
              <RotateCcw className="w-3 h-3" />
              أعد الاستماع
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
